import React, { ReactNode } from 'react';

import {
  Color,
  COLOR_SCHEMA_DARK,
  COLOR_SCHEMA_DARK_LIGHT,
  COLOR_SCHEMA_LIGHT,
  ColorSchema,
  COMPONENT_SIZE_RESPONSIVE,
} from '../../../typings/design-tokens';
import { IComponent } from '../../../typings/generic-component';
import { LiveOnSkyType, MatchPhase, MatchPhaseRound, MatchStatusType } from '../../../typings/match';
import { CARD, CtaTarget, RESULTS, RESULTS_THICK, Source } from '../../../typings/match-row';
import { getLiveMatchTimeLabel, getResult, isLive, isNotEnded, isPreMatch } from '../../../utils/match-status';
import { isLargeOrResponsive } from '../../../utils/sizes';
import { applyColorSchema, getMappedColor, makeClassNames } from '../../../utils/styles';
import { getTeamsAttributes } from '../../../utils/teams';
import Cta from '../../atoms/cta/cta';
import LiveOnSkyBadge from '../../atoms/live-on-sky-badge/live-on-sky-badge';
import ResultLabel from '../../atoms/result-label/result-label';
import RoundGroup from '../../atoms/round-group/round-group';
import Text from '../../atoms/text/text';
import MatchStatusWrapper from '../match-status/match-status-wrapper';
import Team, { IconPosition, ITeam } from '../team/team';

import './match-row.scss';

export interface IMatchRow extends IComponent {
  away: ITeam;
  home: ITeam;
  status: MatchStatusType;
  competitionPhase?: MatchPhase; // BE sends NULL if COMPETITION is LINEAR
  competitionPhaseRound?: MatchPhaseRound; // Andata/Ritorno
  competitionPhaseWinnerTeamId?: string | number | null; // TODO: fix MOCKED DATA and make it MANDATORY as all other IDs!
  cta?: string;
  customCompetitionName?: string | null;
  date?: string;
  externalInterval?: number; // TIMING text to DISPLAY (overrides TIMER/timerInterval)
  footer?: ReactNode | string;
  header?: ReactNode | string;
  id?: string | number; // TODO: Should be only NUMBER after BE close ISS-4186 (and we'll fix our mocked data accordingly)
  matchId?: string | null;
  label?: string | null;
  matchDetailUrl?: string | null;
  link?: string | null; // BE sends NULL value, too
  matchCurrentDateTime?: string;
  round?: number | null;
  roundGroup?: string | null; // BE sends NULL if COMPETITION is LINEAR
  showLiveResult?: boolean;
  statusStartDateTime?: string;
  timerInterval?: number; // SECONDS, not MILLISECONDS
  venue?: string | null;
  liveOnSky?: LiveOnSkyType;
  target?: CtaTarget;
  linkArialabel?: string; // Aria label for inner <a>
}
export interface IMatchRowSource extends IMatchRow {
  type?: Source;
}

const DEFAULT_MATCH_DATE = '';
const DEFAULT_ICON_POSITION: IconPosition = 'right';
const TEXT_COLOR_SCHEMA_DARK: Color = 'light';
const TEXT_COLOR_SCHEMA_LIGHT: Color = 'blue';

const getColorSchemaMapping = () => {
  return {
    [COLOR_SCHEMA_DARK]: TEXT_COLOR_SCHEMA_DARK,
    [COLOR_SCHEMA_DARK_LIGHT]: TEXT_COLOR_SCHEMA_DARK,
    [COLOR_SCHEMA_LIGHT]: TEXT_COLOR_SCHEMA_LIGHT,
  };
};

// HELPERS

const isShowStatus = (status: MatchStatusType, label?: string | null | undefined) =>
  isLive(status) || isNotEnded(status) || label;

const isMatchRowResults = (context: Source) => context === RESULTS || context === RESULTS_THICK;

const classNames = ({
  link,
  cta,
  colorSchema,
  type,
}: {
  link: string | null;
  cta?: string;
  colorSchema: ColorSchema;
  type?: string;
}) =>
  makeClassNames({
    row: [
      'match-row',
      type === CARD && 'match-row--card',
      type === RESULTS_THICK && 'match-row--results-thick',
      `${applyColorSchema('match-row', colorSchema)}`,
    ],
    header: 'match-row__header',
    footer: 'match-row__footer',
    home: ['match-row__home', `${applyColorSchema('match-row__home', colorSchema)}`],
    away: ['match-row__away', `${applyColorSchema('match-row__away', colorSchema)}`],
    teamDesktop: 'match-row__team--desktop',
    teamMobile: 'match-row__team--mobile',
    link: [
      'match-row__link',
      link ? `match-row__link--clickable ${applyColorSchema('match-row__link--clickable', colorSchema)}` : '',
    ],
    status: {
      'match-row__status': true,
      'match-row__status--mouseover': Boolean(link && cta),
    },
    group: 'match-row__group',
    result: 'match-row__result',
    liveOnSky: 'match-row__live-on-sky',
  });

// TODO: add schema dark for: MatchStatus
/**
 * Prop **cta** only visible on desktop mouseover when **link** prop is not empty
 */
const MatchRow = ({
  /* istanbul ignore next: default value */ link = '',
  /* istanbul ignore next: default value */ colorSchema = COLOR_SCHEMA_LIGHT,
  /* istanbul ignore next: default value */ size = COMPONENT_SIZE_RESPONSIVE,
  /* istanbul ignore next: default value */ showLiveResult = true,
  /* istanbul ignore next: default value */ date = DEFAULT_MATCH_DATE,
  /* istanbul ignore next: default value */ type = RESULTS,
  header,
  footer,
  home,
  away,
  status,
  label,
  roundGroup,
  cta,
  trackingData,
  liveOnSky,
  target,
  linkArialabel,
}: IMatchRowSource) => {
  const result = getResult(status, date, home, away, showLiveResult);
  const cn = classNames({ link, cta, colorSchema, type });
  const colorSchemaMapping = getColorSchemaMapping();
  const color = getMappedColor({ colorSchemaMapping, colorSchema });
  const showCTA = link && cta && isLargeOrResponsive(size);

  const showStatus = isLargeOrResponsive(size) && isShowStatus(status, getLiveMatchTimeLabel(status) || label);
  const { desktop, mobile } = getTeamsAttributes(home, away, roundGroup);

  const shouldRenderLiveOnSky = isMatchRowResults(type) && liveOnSky && (isLive(status) || isPreMatch(status));
  const shouldRenderHeader = header && isMatchRowResults(type) && !shouldRenderLiveOnSky;
  const shouldRenderStatus = isMatchRowResults(type) && showStatus && showLiveResult && !shouldRenderLiveOnSky;
  const shouldRenderRoundGroup = isMatchRowResults(type) && roundGroup;

  return (
    <tr className={cn.row}>
      <td className={cn.link}>
        {link && (
          <Cta href={link} trackingData={trackingData} target={target} hasWrapper={false} label={linkArialabel} />
        )}
      </td>

      {/* HEADER */}
      <td className={cn.header} data-state={shouldRenderHeader ? 'filled' : 'empty'} aria-hidden={!shouldRenderHeader}>
        {shouldRenderHeader && (
          <Text color={color} fontSize="12" fontWeight="400">
            {header}
          </Text>
        )}
      </td>

      {/* STATUS */}
      <td className={cn.status} data-state={shouldRenderStatus || shouldRenderLiveOnSky ? 'filled' : 'empty'}>
        {shouldRenderStatus && <MatchStatusWrapper status={status} backgroundColor={false} />}
        {showCTA && <Cta colorSchema={colorSchema}>{cta}</Cta>}
        {shouldRenderLiveOnSky && (
          <div className={cn.liveOnSky}>
            <LiveOnSkyBadge liveOnSky={liveOnSky} />
          </div>
        )}
      </td>

      {/* ROUND GROUP */}
      <td className={cn.group} aria-hidden={!shouldRenderHeader}>
        {shouldRenderRoundGroup && <RoundGroup name={roundGroup} colorSchema={colorSchema} size={size} />}
      </td>

      {/* HOME */}

      <td className={cn.home}>
        <span className={cn.teamDesktop}>
          <Team
            {...home}
            color={color}
            name={desktop.homeName}
            clickable={typeof home.homepageUrl === 'string'}
            fontSize={desktop.fontSize}
            iconPosition={DEFAULT_ICON_POSITION}
            scoreboard={[]}
          />
        </span>
        <span className={cn.teamMobile}>
          <Team
            {...home}
            color={color}
            name={mobile.homeName}
            clickable={typeof home.homepageUrl === 'string'}
            fontSize={mobile.fontSize}
            iconPosition={DEFAULT_ICON_POSITION}
            scoreboard={[]}
          />
        </span>
      </td>

      <td className={cn.result}>
        {
          <ResultLabel itemState={status} colorSchema={colorSchema}>
            {result.text}
          </ResultLabel>
        }
      </td>

      <td className={cn.away}>
        <span className={cn.teamDesktop}>
          <Team
            {...away}
            color={color}
            name={desktop.awayName}
            clickable={typeof away.homepageUrl === 'string'}
            fontSize={desktop.fontSize}
            scoreboard={[]}
          />
        </span>
        <span className={cn.teamMobile}>
          <Team
            {...away}
            color={color}
            name={mobile.awayName}
            clickable={typeof away.homepageUrl === 'string'}
            fontSize={mobile.fontSize}
            scoreboard={[]}
          />
        </span>
      </td>

      {/* FOOTER */}
      <td className={cn.footer} data-state={footer ? 'filled' : 'empty'} aria-hidden={Boolean(footer)}>
        {isMatchRowResults(type) && footer && (
          <Text color={color} fontSize="12" fontWeight="400">
            {footer}
          </Text>
        )}
      </td>
    </tr>
  );
};

export default MatchRow;
